// ============================================
// My Results — Patient Dashboard
//
// Route: /my-results
// Shows past journeys, protocols, and orders for
// authenticated patients. Redirects to /login if
// not authenticated.
//
// Design system: light theme, Instrument Serif titles,
// DM Sans body, Rose accent, 4px radius, WCAG AA.
// No framer-motion.
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, LogOut, Loader2, FlaskConical, FileText,
  Clock, CheckCircle2, AlertCircle, ChevronRight, Plus,
} from 'lucide-react';
import {
  getCurrentPatientAuth,
  onPatientAuthStateChanged,
  getPatientJourneys,
  type PatientJourneySummary,
  type PatientAuthState,
} from '../../services/auth/authUpgradeService';
import { signOut } from 'firebase/auth';
import { auth } from '../../services/firebase';
import { onLogout } from '../../services/sessionService';
import { getStepLabel } from '../../config/journeyConfig';

const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: typeof CheckCircle2 }> = {
  complete: { label: 'Complete', color: 'text-[#059669]', bgColor: 'bg-[#ECFDF5]', icon: CheckCircle2 },
  active: { label: 'In Progress', color: 'text-[#D97706]', bgColor: 'bg-[#FFFBEB]', icon: Clock },
  abandoned: { label: 'Incomplete', color: 'text-[#A1A1AA]', bgColor: 'bg-[#F4F4F5]', icon: AlertCircle },
};

function formatDate(iso: string): string {
  if (!iso) return '--';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatTime(iso: string): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export function MyResults() {
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<PatientAuthState>(getCurrentPatientAuth());
  const [journeys, setJourneys] = useState<PatientJourneySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Listen for auth state
  useEffect(() => {
    const unsub = onPatientAuthStateChanged((state) => {
      setAuthState(state);
      if (!state.isAuthenticated) {
        // Redirect to login if not authenticated
        navigate('/login', { replace: true });
      }
    });
    return unsub;
  }, [navigate]);

  // Fetch journeys when authenticated
  useEffect(() => {
    if (!authState.isAuthenticated || !authState.uid) return;

    setLoading(true);
    setError('');

    getPatientJourneys(authState.uid)
      .then((data) => {
        setJourneys(data);
        setLoading(false);
      })
      .catch((err) => {
        console.warn('[MyResults] Failed to load journeys:', err);
        setError('Failed to load your results. Please try again.');
        setLoading(false);
      });
  }, [authState.uid, authState.isAuthenticated]);

  async function handleSignOut() {
    try {
      await signOut(auth);
      // Rotate session on logout to limit XSS exposure window
      onLogout();
      navigate('/login', { replace: true });
    } catch (err) {
      console.warn('[MyResults] Sign out failed:', err);
    }
  }

  // Wait for auth check
  if (!authState.isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <div className="text-center">
          <Loader2 size={24} className="animate-spin text-[#E11D48] mx-auto mb-3" />
          <p className="text-[14px] text-[#52525B]">Checking your account...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-[#E4E4E7] px-4 py-3">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="text-[#A1A1AA] hover:text-[#18181B] transition-colors"
              aria-label="Back to home"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1
                className="text-lg text-[#18181B]"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                My Results
              </h1>
              <p className="text-[#A1A1AA] text-[13px]">{authState.email}</p>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            className="flex items-center gap-1.5 text-[13px] text-[#A1A1AA] hover:text-[#52525B] transition-colors"
          >
            <LogOut size={15} />
            Sign out
          </button>
        </div>
      </div>

      <div className="px-4 py-6 max-w-2xl mx-auto">
        {/* Quick stats */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-white border border-[#E4E4E7] rounded-[4px] p-4">
            <div className="flex items-center gap-2 mb-1">
              <FlaskConical size={15} className="text-[#E11D48]" />
              <span className="text-[13px] text-[#A1A1AA] font-medium">Analyses</span>
            </div>
            <p className="text-2xl font-semibold text-[#18181B]">
              {loading ? '--' : journeys.length}
            </p>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-[4px] p-4">
            <div className="flex items-center gap-2 mb-1">
              <FileText size={15} className="text-[#E11D48]" />
              <span className="text-[13px] text-[#A1A1AA] font-medium">Protocols</span>
            </div>
            <p className="text-2xl font-semibold text-[#18181B]">
              {loading ? '--' : journeys.filter((j) => j.protocolId).length}
            </p>
          </div>
        </div>

        {/* Section heading */}
        <div className="flex items-center justify-between mb-3">
          <h2
            className="text-[16px] text-[#18181B]"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            Your Analyses
          </h2>
          <Link
            to="/landing"
            className="flex items-center gap-1 text-[13px] text-[#E11D48] hover:text-[#BE123C] font-medium transition-colors"
          >
            <Plus size={14} />
            New Analysis
          </Link>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <Loader2 size={24} className="animate-spin text-[#E11D48] mx-auto mb-3" />
            <p className="text-[14px] text-[#52525B]">Loading your results...</p>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="flex items-start gap-2 text-[#DC2626] text-[13px] bg-[#FEF2F2] px-4 py-3 rounded-[4px] border border-[#FEE2E2] mb-4">
            <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && journeys.length === 0 && (
          <div className="text-center py-16 bg-white border border-[#E4E4E7] rounded-[4px]">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#F4F4F5] mb-4">
              <FlaskConical size={24} className="text-[#A1A1AA]" />
            </div>
            <h3
              className="text-[16px] text-[#18181B] mb-2"
              style={{ fontFamily: "'Instrument Serif', serif" }}
            >
              No results yet
            </h3>
            <p className="text-[14px] text-[#52525B] mb-4 max-w-xs mx-auto">
              Upload your lab results to get a personalized protocol and biomarker analysis.
            </p>
            <Link
              to="/landing"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[4px] bg-[#E11D48] text-white text-[14px] font-medium hover:bg-[#BE123C] transition-colors"
            >
              <Plus size={16} />
              Start Your First Analysis
            </Link>
          </div>
        )}

        {/* Journey list */}
        {!loading && !error && journeys.length > 0 && (
          <div className="space-y-3">
            {journeys.map((journey) => {
              const statusConfig = STATUS_CONFIG[journey.status] || STATUS_CONFIG.abandoned;
              const StatusIcon = statusConfig.icon;

              return (
                <div
                  key={journey.sessionId}
                  className="bg-white border border-[#E4E4E7] rounded-[4px] p-4 hover:border-[#E11D48]/30 transition-colors cursor-pointer group"
                  onClick={() => {
                    try {
                      localStorage.setItem('hemologix_session_id', journey.sessionId);
                    } catch { /* localStorage unavailable */ }
                    navigate('/app');
                  }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-[4px] bg-[#E11D48]/10 flex items-center justify-center">
                        <FlaskConical size={15} className="text-[#E11D48]" />
                      </div>
                      <div>
                        <p className="text-[14px] font-medium text-[#18181B]">
                          Lab Analysis
                        </p>
                        <p className="text-[12px] text-[#A1A1AA]">
                          {formatDate(journey.createdAt)}
                          {' '}
                          {formatTime(journey.createdAt)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-medium ${statusConfig.color} ${statusConfig.bgColor}`}>
                        <StatusIcon size={12} />
                        {statusConfig.label}
                      </span>
                      <ChevronRight size={16} className="text-[#A1A1AA] group-hover:text-[#E11D48] transition-colors" />
                    </div>
                  </div>

                  {/* Details row */}
                  <div className="flex items-center gap-4 mt-2 pl-10 text-[13px] text-[#52525B]">
                    {journey.triageData && (
                      <span>
                        {journey.triageData.sex === 'male' ? 'Male' : 'Female'}, {journey.triageData.age}y
                      </span>
                    )}
                    <span className="text-[#E4E4E7]">|</span>
                    <span>Step: {getStepLabel(journey.currentStep)}</span>
                    {journey.protocolId && (
                      <>
                        <span className="text-[#E4E4E7]">|</span>
                        <span className="text-[#059669] font-medium">Protocol generated</span>
                      </>
                    )}
                  </div>

                  {/* Last activity */}
                  {journey.lastActivityAt && journey.lastActivityAt !== journey.createdAt && (
                    <p className="text-[12px] text-[#A1A1AA] mt-2 pl-10">
                      Last activity: {formatDate(journey.lastActivityAt)} {formatTime(journey.lastActivityAt)}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Bottom padding */}
        <div className="h-8" />
      </div>
    </div>
  );
}
